import { useEffect, useState } from 'react'
import { immer } from 'zustand/middleware/immer'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { OffersType } from 'components/Dashboard/InfiniteOffers/InfiniteOffers.types'

export type OfferStore = {
  offersType: OffersType
  setOffersType: (type: OffersType) => void
  lastViewedReceived: { [key: string]: string } // key is the active address
  setLastViewedReceived: (address: string | undefined, time: string) => void
  lastViewedSent: { [key: string]: string } // key is the active address
  setLastViewedSent: (address: string | undefined, time: string) => void
}

const emptyState: OfferStore = {
  offersType: 'received',
  setOffersType: () => {
    return
  },
  lastViewedReceived: {},
  setLastViewedReceived: () => {
    return
  },
  lastViewedSent: {},
  setLastViewedSent: () => {
    return
  }
}

const usePersistedStore = create<OfferStore>()(
  immer(
    persist(
      devtools((set) => ({
        offersType: 'received',
        setOffersType: (type: OffersType) => {
          set((state) => {
            state.offersType = type
          })
        },
        lastViewedReceived: {},
        setLastViewedReceived: (address, time) => {
          set((state) => {
            if (!address) {
              throw new Error('Address missing.')
            }

            state.lastViewedReceived[address] = time
          })
        },
        lastViewedSent: {},
        setLastViewedSent: (address, time) => {
          if (!address) {
            throw new Error('Address missing.')
          }

          set((state) => {
            state.lastViewedSent[address] = time
          })
        }
      })),
      {
        name: 'nfd-offer-store',
        version: 2,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        migrate: (persistedState: any, version) => {
          const activeAddress: string =
            JSON.parse(localStorage.getItem('txnlab-use-wallet') || '')?.state?.activeAccount
              ?.address || ''

          if (version === 1 && activeAddress) {
            const prevLastViewedSent = persistedState.lastViewedSent
            const prevLastViewedReceived = persistedState.lastViewedReceived

            persistedState.lastViewedSent = {}
            persistedState.lastViewedSent[activeAddress] = prevLastViewedSent

            persistedState.lastViewedReceived = {}
            persistedState.lastViewedReceived[activeAddress] = prevLastViewedReceived
          }

          return persistedState as OfferStore
        }
      }
    )
  )
)

// @see https://github.com/pmndrs/zustand/issues/1145#issuecomment-1207233036
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useOfferStore = ((selector: any, compare: any) => {
  const store = usePersistedStore(selector, compare)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])

  return hydrated ? store : selector(emptyState)
}) as typeof usePersistedStore
